import { isBefore } from 'date-fns';
import { get } from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useClient } from 'urql';

import { oryLoginFlow, oryWhoAmI } from '@revelio/auth';
import { useMaintenanceFlag, userTracking } from '@revelio/core';
import { validateEmail } from '@revelio/iso-utility';

import { RedirectContext } from '../../redirect-context';
import {
  dashboardUrl,
  isCreateAccountEnabled,
  isWilsonBrand,
  wilsonButtonVars,
} from '../../wilson-brand';
import { getUserTrackingData } from '../utils/mixpanel-helper';
import {
  getOidcLoginChallenge,
  isOidcLoginRequest,
  oidcLogin,
} from '../utils/oidc-login';
import { carriedAuthParams } from './auth-params';
import {
  SECTION_SPACING,
  ThemeHeading,
  ThemeLink,
  ThemeTagline,
} from './theme/theme';
import {
  Button,
  Field,
  IconButton,
  LinkButton,
  Stack,
  TextInput,
  uiStyles,
} from './ui';
import { ViewIcon, ViewOffIcon } from './ui/icons';

interface FormValues {
  email: string;
  password: string;
}

function LoginForm() {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { isOn: maintenanceOn, message: maintenanceMessage } =
    useMaintenanceFlag();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();
  const navigate = useNavigate();
  // Carried onto internal auth links so `?s=website` (light theme) survives
  // the login <-> signup hop.
  const { search } = useLocation();

  const [searchParams] = useSearchParams();
  const [redirect] = useContext(RedirectContext);
  const redirect_uri = searchParams.get('redirect_uri');
  const wid = searchParams.get('wid');
  const isOidc = isOidcLoginRequest(searchParams);

  const gqlClient = useClient();

  const checkStatus = useCallback(async () => {
    const user = await oryWhoAmI();
    if (!user.active) {
      return;
    }
    // A trial user who bailed before the "tell us about yourself" step must
    // not bounce straight to the dashboard on an existing session either —
    // personalization_completed is only stamped once /interested-trial has
    // been submitted. A failed lookup falls through to the dashboard rather
    // than stranding the user here.
    if (!isWilsonBrand) {
      try {
        const tracking = await getUserTrackingData({
          userId: get(user, 'identity.id'),
          creationDate: get(user, 'identity.created_at'),
          gqlClient,
        });
        if (tracking?.trial && !tracking.personalizationCompletedAt) {
          const carried = carriedAuthParams(searchParams).toString();
          navigate(carried ? `/personalize?${carried}` : '/personalize');
          return;
        }
      } catch (e) {
        console.info(e, 'could not check personalization state');
      }
    }
    const url = new URL(redirect_uri || dashboardUrl);
    url.searchParams.append('wid', wid || '');
    window.location.replace(url.toString());
  }, [redirect_uri, wid, gqlClient, navigate, searchParams]);

  useEffect(() => {
    if (isOidc) {
      return;
    }
    checkStatus();
  }, [checkStatus, isOidc]);

  const clearForm = () => {
    setErrorMessage('');
  };

  const handleToggleShowPassword = () => setShowPassword((prevVal) => !prevVal);

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  const oryLogin = useCallback(
    async ({ email, password }: FormValues) => {
      const oryResponse = await oryLoginFlow({
        email,
        password,
      });

      const userId = get(oryResponse, 'ory.session.identity.id');
      const creationDate = get(oryResponse, 'ory.session.identity.created_at');

      let isTrialUser = true;
      let userTrackingData: Awaited<ReturnType<typeof getUserTrackingData>>;
      try {
        userTrackingData = await getUserTrackingData({
          userId,
          creationDate,
          gqlClient,
        });
        isTrialUser = userTrackingData?.trial ?? isTrialUser;
        userTrackingData && userTracking.setUser(userTrackingData);
        userTracking.login();
      } catch (e) {
        console.info(e, 'could not send tracking data');
      }

      clearForm();

      const userTrialExpirationDate =
        oryResponse.ory.session.identity.metadata_public.expiration_date;
      const hasTrialExpired =
        userTrialExpirationDate && isTrialUser
          ? isBefore(new Date(userTrialExpirationDate), new Date())
          : false;
      if (!isWilsonBrand && hasTrialExpired) {
        const trialExpiredParams = carriedAuthParams(searchParams);
        trialExpiredParams.set(
          'name',
          oryResponse.ory.session.identity.traits.name
        );
        trialExpiredParams.set(
          'email',
          oryResponse.ory.session.identity.traits.email
        );
        navigate(`/trial-expired?${trialExpiredParams.toString()}`);
        return;
      }

      // A live-trial user with no personalization stamp bailed before the
      // "tell us about yourself" step — route them there, not the dashboard.
      // Requires a successful lookup so a network blip can't strand anyone.
      if (
        !isWilsonBrand &&
        userTrackingData?.trial &&
        !userTrackingData.personalizationCompletedAt
      ) {
        const carried = carriedAuthParams(searchParams).toString();
        navigate(carried ? `/personalize?${carried}` : '/personalize');
        return;
      }

      if (redirect) {
        window.location.assign(redirect);
      } else {
        window.location.assign(dashboardUrl);
      }
    },
    [gqlClient, redirect, navigate, searchParams]
  );

  const handleOidcLogIn = async ({ email, password }: FormValues) => {
    const loginChallenge = getOidcLoginChallenge(searchParams);
    if (!loginChallenge) {
      throw new Error('OIDC login is missing login challenge');
    }

    const oidcResponse = await oidcLogin({
      username: email,
      password,
      login_challenge: loginChallenge,
    });
    clearForm();
    window.location.assign(oidcResponse.redirect_to);
  };

  const handleLogIn = handleSubmit(async ({ email, password }) => {
    setLoading(true);
    try {
      if (isOidc) {
        await handleOidcLogIn({ email, password });
      } else {
        await oryLogin({ email, password });
      }
    } catch (err) {
      console.error(err);
      setErrorMessage('Failed to authenticate.');
    }
    setLoading(false);
  });

  return (
    <Stack gap={SECTION_SPACING} fullWidth>
      <Stack gap="12px">
        <ThemeHeading>Log in</ThemeHeading>
        {isCreateAccountEnabled && (
          <ThemeTagline>
            Don't have an account?{' '}
            <ThemeLink href={`/signup${search}`}>Create account</ThemeLink>
          </ThemeTagline>
        )}
      </Stack>

      <form onSubmit={handleLogIn}>
        <Stack gap="16px">
          <Field error={errors['email']?.message}>
            <TextInput
              // `username` (not `email`) on purpose: it is the token password
              // managers key the SAVED credential pair off — `email` makes
              // some managers offer generic contact emails instead.
              type="email"
              placeholder="Email"
              aria-label="Email"
              autoComplete="username"
              invalid={!!errors['email']}
              {...register('email', {
                required: 'Email is required',
                validate: {
                  emailSyntax: (email: string) =>
                    validateEmail(email) || 'Valid email is required',
                },
              })}
            />
          </Field>
          <Field error={errors['password']?.message}>
            <TextInput
              placeholder="Password"
              aria-label="Password"
              autoComplete="current-password"
              type={showPassword ? 'text' : 'password'}
              invalid={!!errors['password']}
              rightSlot={
                <IconButton
                  aria-label="Toggle view password"
                  onClick={handleToggleShowPassword}
                >
                  {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                </IconButton>
              }
              {...register('password', {
                required: 'Password is required',
              })}
            />
          </Field>
          <Button
            style={{
              marginTop: '8px',
              ...(isWilsonBrand ? wilsonButtonVars : {}),
            }}
            disabled={loading}
            type="submit"
          >
            Log In
          </Button>
        </Stack>
        {errorMessage && <p className={uiStyles.formError}>{errorMessage}</p>}
        {maintenanceOn && (
          <p className={uiStyles.formError} role="alert">
            {maintenanceMessage}
          </p>
        )}
      </form>

      <Stack gap="12px" align="flex-start">
        {!isWilsonBrand && !isOidc && (
          <LinkButton onClick={() => navigate('/login-sso')}>
            Log in with SSO
          </LinkButton>
        )}
        <ThemeLink href={`/recovery${search}`}>Forgot password?</ThemeLink>
      </Stack>
    </Stack>
  );
}

export default LoginForm;
